class DtailsBooking extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.today = this.startOfDay(new Date());
    this.lastBookableDate = this.addDays(this.today, 30);
    this.currentMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    this.firstMonth = new Date(this.currentMonth);
    this.lastMonth = new Date(this.lastBookableDate.getFullYear(), this.lastBookableDate.getMonth(), 1);
    this.availability = new Map();
    this.selectedDate = null;
    this.selectedTime = null;
    this.selectedSlotId = null;
    this.requestId = null;
    this.availabilityLoadedAt = 0;
    this.availabilityMaxAge = 55 * 60 * 1000;
    this.locale = this.dataset.locale || document.documentElement.lang || 'da-DK';

    this.subject = this.querySelector('[data-booking-subject]');
    this.days = this.querySelector('[data-booking-days]');
    this.monthLabel = this.querySelector('[data-booking-month]');
    this.previousButton = this.querySelector('[data-booking-previous-month]');
    this.nextButton = this.querySelector('[data-booking-next-month]');
    this.hint = this.querySelector('[data-booking-hint]');
    this.times = this.querySelector('[data-booking-times]');
    this.selectedDateLabel = this.querySelector('[data-booking-selected-date]');
    this.summary = this.querySelector('[data-booking-summary]');
    this.form = this.querySelector('[data-booking-form]');
    this.submitButton = this.querySelector('[data-booking-submit]');
    this.status = this.querySelector('[data-booking-status]');
    this.confirmation = this.querySelector('[data-booking-confirmation]');

    this.subject?.addEventListener('change', () => this.onSubjectChange());
    this.previousButton?.addEventListener('click', () => this.changeMonth(-1));
    this.nextButton?.addEventListener('click', () => this.changeMonth(1));
    this.querySelectorAll('[data-booking-back]').forEach((button) => {
      button.addEventListener('click', () => this.showStep(button.dataset.bookingBack));
    });
    this.form?.addEventListener('submit', (event) => this.submitBooking(event));

    this.renderCalendar();
  }

  startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  addDays(date, amount) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
  }

  dateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  parseDate(key) {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  async onSubjectChange() {
    this.selectedDate = null;
    this.selectedTime = null;
    this.selectedSlotId = null;
    this.requestId = null;
    this.availabilityLoadedAt = 0;
    this.availability.clear();

    if (!this.subject.value) {
      this.hint.textContent = this.dataset.selectSubjectMessage;
      this.renderCalendar();
      return;
    }

    this.hint.textContent = this.dataset.loadingAvailabilityMessage;

    try {
      await this.loadAvailability();
      this.hint.textContent = this.availability.size
        ? this.dataset.availableHintMessage
        : this.dataset.noAvailabilityMessage;
    } catch (error) {
      this.hint.textContent = this.dataset.availabilityErrorMessage;
      console.error('[DTAILS booking] Availability error:', error);
    }

    this.renderCalendar();
  }

  async loadAvailability() {
    const endpoint = this.dataset.availabilityEndpoint?.trim();

    if (endpoint) {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('subject', this.subject.value);
      url.searchParams.set('from', this.dateKey(this.today));
      url.searchParams.set('to', this.dateKey(this.lastBookableDate));

      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Availability request failed (${response.status})`);
      const payload = await response.json();
      this.setAvailability(payload);
      this.availabilityLoadedAt = Date.now();
      return;
    }

    if (this.dataset.demoMode === 'true') {
      this.createDemoAvailability();
      this.availabilityLoadedAt = Date.now();
    }
  }

  setAvailability(payload) {
    const dates = payload.availableDates || payload.availability || {};

    if (Array.isArray(dates)) {
      dates.forEach((entry) => {
        const slots = entry?.slots || entry?.times;
        if (entry?.date && Array.isArray(slots) && slots.length) {
          this.availability.set(entry.date, this.normalizeSlots(slots));
        }
      });
      return;
    }

    Object.entries(dates).forEach(([date, slots]) => {
      if (Array.isArray(slots) && slots.length) this.availability.set(date, this.normalizeSlots(slots));
    });
  }

  normalizeSlots(slots) {
    return slots
      .map((slot) => {
        if (typeof slot === 'string') return { time: slot, slotId: null };
        if (slot && typeof slot.time === 'string') return { time: slot.time, slotId: slot.slotId || null };
        return null;
      })
      .filter(Boolean);
  }

  createDemoAvailability() {
    const baseSlots = ['09:00', '09:30', '10:30', '11:00', '13:00', '13:30', '14:30', '15:00'];

    for (let offset = 1; offset <= 30; offset += 1) {
      const date = this.addDays(this.today, offset);
      const weekday = date.getDay();
      if (weekday === 0 || weekday === 6) continue;

      const filtered = baseSlots.filter((_, index) => (index + offset + this.subject.value.length) % 3 !== 0);
      this.availability.set(
        this.dateKey(date),
        filtered.map((time) => ({ time, slotId: `demo-${this.dateKey(date)}-${time}` }))
      );
    }
  }

  renderCalendar() {
    if (!this.days || !this.monthLabel) return;

    this.monthLabel.textContent = new Intl.DateTimeFormat(this.locale, {
      month: 'long',
      year: 'numeric',
    }).format(this.currentMonth);

    this.previousButton.disabled = this.currentMonth <= this.firstMonth;
    this.nextButton.disabled = this.currentMonth >= this.lastMonth;
    this.days.replaceChildren();

    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let index = 0; index < 42; index += 1) {
      const dayNumber = index - firstWeekday + 1;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dtails-booking__day';

      if (dayNumber < 1 || dayNumber > daysInMonth) {
        button.classList.add('dtails-booking__day--outside');
        button.disabled = true;
        button.tabIndex = -1;
      } else {
        const date = new Date(year, month, dayNumber);
        const key = this.dateKey(date);
        const available = this.subject?.value && this.availability.has(key);
        const inRange = date >= this.today && date <= this.lastBookableDate;
        button.textContent = String(dayNumber);
        button.disabled = !available || !inRange;
        button.setAttribute('aria-label', new Intl.DateTimeFormat(this.locale, { dateStyle: 'full' }).format(date));

        if (available && inRange) {
          button.classList.add('dtails-booking__day--available');
          button.addEventListener('click', () => this.selectDate(key));
        }
      }

      this.days.append(button);
    }
  }

  changeMonth(direction) {
    const next = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + direction, 1);
    if (next < this.firstMonth || next > this.lastMonth) return;
    this.currentMonth = next;
    this.renderCalendar();
  }

  selectDate(key) {
    this.selectedDate = key;
    this.selectedTime = null;
    this.selectedSlotId = null;
    this.requestId = null;
    const date = this.parseDate(key);
    this.selectedDateLabel.textContent = new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);

    this.times.replaceChildren();
    (this.availability.get(key) || []).forEach((slot) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dtails-booking__time btn btn--secondary';
      button.textContent = slot.time;
      button.addEventListener('click', () => this.selectTime(slot));
      this.times.append(button);
    });

    this.showStep('times');
  }

  selectTime(slot) {
    this.selectedTime = slot.time;
    this.selectedSlotId = slot.slotId;
    this.requestId = null;
    const date = this.parseDate(this.selectedDate);
    const formattedDate = new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
    this.summary.textContent = new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date.getFullYear(), date.getMonth(), date.getDate(), ...slot.time.split(':').map(Number)));
    this.status.textContent = '';
    this.showStep('details');
    this.form?.querySelector('input')?.focus({ preventScroll: true });
  }

  showStep(step) {
    this.querySelectorAll('[data-booking-step]').forEach((panel) => {
      panel.hidden = panel.dataset.bookingStep !== step;
    });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async submitBooking(event) {
    event.preventDefault();
    if (!this.form.reportValidity() || !this.selectedDate || !this.selectedTime) return;

    if (
      this.dataset.availabilityEndpoint?.trim() &&
      (!this.availabilityLoadedAt || Date.now() - this.availabilityLoadedAt >= this.availabilityMaxAge)
    ) {
      await this.refreshAvailability(this.dataset.availabilityExpiredMessage);
      return;
    }

    if (!this.requestId) {
      this.requestId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    const formData = new FormData(this.form);
    const payload = {
      subject: this.subject.value,
      slotId: this.selectedSlotId,
      date: this.selectedDate,
      time: this.selectedTime,
      duration: Number(this.dataset.duration || 30),
      timeZone: this.dataset.timeZone || 'Europe/Copenhagen',
      firstName: formData.get('first_name'),
      lastName: formData.get('last_name'),
      email: formData.get('email'),
      meetingSubject: formData.get('meeting_subject'),
      requestId: this.requestId,
    };

    const endpoint = this.dataset.bookingEndpoint?.trim();
    if (!endpoint) {
      this.dispatchEvent(new CustomEvent('dtails:booking-submit', { detail: payload, bubbles: true }));
      this.setStatus(this.dataset.integrationMessage, false);
      return;
    }

    this.setLoading(true);
    this.setStatus(this.dataset.bookingLoadingMessage, false);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));

        if (response.status === 409 && errorPayload.code === 'SLOT_UNAVAILABLE') {
          await this.refreshAvailability(this.dataset.slotUnavailableMessage);
          return;
        }

        if (response.status === 400) {
          await this.refreshAvailability(this.dataset.availabilityExpiredMessage);
          return;
        }

        throw new Error(`Booking request failed (${response.status})`);
      }

      this.form.hidden = true;
      this.confirmation.hidden = false;
    } catch (error) {
      this.setStatus(this.dataset.bookingErrorMessage, true);
      console.error('[DTAILS booking] Booking error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async refreshAvailability(message) {
    this.selectedDate = null;
    this.selectedTime = null;
    this.selectedSlotId = null;
    this.requestId = null;
    this.availabilityLoadedAt = 0;
    this.availability.clear();
    this.currentMonth = new Date(this.firstMonth);
    this.hint.textContent = this.dataset.loadingAvailabilityMessage;
    this.showStep('calendar');
    this.renderCalendar();

    try {
      await this.loadAvailability();
      this.hint.textContent = message;
    } catch (error) {
      this.hint.textContent = this.dataset.availabilityErrorMessage;
      console.error('[DTAILS booking] Availability refresh error:', error);
    }

    this.renderCalendar();
  }

  setLoading(loading) {
    this.submitButton.disabled = loading;
    this.submitButton.querySelector('.btn__text').textContent = loading
      ? this.dataset.bookingLoadingMessage
      : this.dataset.bookButtonLabel;
  }

  setStatus(message, error) {
    this.status.textContent = message;
    this.status.classList.toggle('dtails-booking__status--error', error);
  }

}

if (!customElements.get('dtails-booking')) {
  customElements.define('dtails-booking', DtailsBooking);
}
